import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

export type ExpenseCustomFieldType =
  | "text"
  | "number"
  | "date"
  | "select"
  | "checkbox"
  | "textarea";

export interface ExpenseCustomFieldRecord {
  client_id: string;
  field_id: string;
  label: string;
  field_type: ExpenseCustomFieldType;
  options?: string[];
  applies_to_expense_types?: ("OPEX" | "CAPEX")[];
  display_order?: number;
  created_at: string;
  updated_at?: string;
}

export interface CreateExpenseCustomFieldBody {
  label: string;
  field_type: ExpenseCustomFieldType;
  options?: string[];
  applies_to_expense_types?: ("OPEX" | "CAPEX")[];
  display_order?: number;
}

export interface UpdateExpenseCustomFieldBody {
  label?: string;
  field_type?: ExpenseCustomFieldType;
  options?: string[];
  applies_to_expense_types?: ("OPEX" | "CAPEX")[];
  display_order?: number;
}

interface State {
  list: {
    data: ExpenseCustomFieldRecord[];
    loading: boolean;
    error: string | null;
    loaded: boolean;
  };
}

const initialState: State = {
  list: { data: [], loading: false, error: null, loaded: false },
};

export const fetchExpenseCustomFields = createAsyncThunk<
  ExpenseCustomFieldRecord[],
  void
>("expenseCustomFields/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await ipponApi.get(`${BaseURL}/api/v1/expense-custom-fields`);
    return res.data?.data?.items || [];
  } catch {
    return rejectWithValue("カスタムフィールドの取得に失敗しました");
  }
});

export const createExpenseCustomField = createAsyncThunk<
  ExpenseCustomFieldRecord,
  CreateExpenseCustomFieldBody
>("expenseCustomFields/create", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/expense-custom-fields`,
      body,
    );
    return res.data.data;
  } catch {
    return rejectWithValue("カスタムフィールドの登録に失敗しました");
  }
});

export const updateExpenseCustomField = createAsyncThunk<
  ExpenseCustomFieldRecord,
  { field_id: string; body: UpdateExpenseCustomFieldBody }
>(
  "expenseCustomFields/update",
  async ({ field_id, body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.put(
        `${BaseURL}/api/v1/expense-custom-fields/${field_id}`,
        body,
      );
      return res.data.data;
    } catch {
      return rejectWithValue("カスタムフィールドの更新に失敗しました");
    }
  },
);

export const deleteExpenseCustomField = createAsyncThunk<
  { field_id: string },
  string
>("expenseCustomFields/delete", async (field_id, { rejectWithValue }) => {
  try {
    await ipponApi.delete(`${BaseURL}/api/v1/expense-custom-fields/${field_id}`);
    return { field_id };
  } catch {
    return rejectWithValue("カスタムフィールドの削除に失敗しました");
  }
});

const slice = createSlice({
  name: "expenseCustomFields",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchExpenseCustomFields.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchExpenseCustomFields.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data = action.payload;
        state.list.loaded = true;
      })
      .addCase(fetchExpenseCustomFields.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = (action.payload as string) || null;
      })
      .addCase(createExpenseCustomField.fulfilled, (state, action) => {
        state.list.data.push(action.payload);
      })
      .addCase(updateExpenseCustomField.fulfilled, (state, action) => {
        const idx = state.list.data.findIndex(
          (f) => f.field_id === action.payload.field_id,
        );
        if (idx >= 0) state.list.data[idx] = action.payload;
      })
      .addCase(deleteExpenseCustomField.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter(
          (f) => f.field_id !== action.payload.field_id,
        );
      });
  },
});

export default slice.reducer;
